import type { Persona } from '@/shared/types';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import {
  CRO_STATUS_LINE,
  PLANT_ROLES,
  SOURCE_EXTRACTS_LINE,
} from '@/versions/v1/shared/bunker-hill-seed';
import {
  defaultPlantRole,
  plantRoleIdFromPersona,
} from '@/versions/v1/modules/overview/Scene.transform';

type ProductStatusState = 'online' | 'pending';

export type ProductStatusChip = {
  label: string;
  state: ProductStatusState;
  /** Full seed segment when the on-glass label is compressed for chrome density. */
  title?: string;
  /** True when the chip's label must stay visible even though `state` is
   * `online` -- the chrome collapses "online" labels to the dot alone by
   * default (nothing to say), but a cadence switch (LIMS: OSA online) is a
   * fact worth naming even though the dot is green (SA-F5 fix round 1). */
  labelVisible?: boolean;
};

export type ProductBarIdentity = {
  initial: string;
  label: string;
  title: string;
};

/** Deck Field Operator contacts — only named individuals in the corpus. */
const DECK_CONTACT_NAMES: Partial<Record<PlantRoleId, string>> = {
  metallurgist: 'S. Park',
  'senior-metallurgist': 'L. Chen',
  'ops-supervisor': 'D. Lee',
};

/** ui-screens §1.2 product-bar subset — derived from the source-status seed line. */
export function productStatusChips(cadence?: 'lab' | 'osa'): ProductStatusChip[] {
  const segments = CRO_STATUS_LINE.split(' · ').map((part) => part.trim());
  const byPrefix: Partial<
    Record<'dcs' | 'historian' | 'lims' | 'design' | 'ai', ProductStatusChip>
  > = {};
  for (const segment of segments) {
    const lower = segment.toLowerCase();
    if (lower.startsWith('dcs')) byPrefix.dcs = { label: 'DCS online', state: 'online' };
    if (lower.startsWith('historian')) {
      byPrefix.historian = { label: 'Historian · connecting', state: 'pending', title: segment };
    }
    if (lower.includes('lims')) {
      byPrefix.lims = { label: segment, state: 'pending' };
    }
    if (lower.startsWith('design basis') || lower.startsWith('scada')) {
      byPrefix.design = { label: 'Design basis loaded', state: 'online' };
    }
    if (lower.startsWith('ai engine')) {
      byPrefix.ai = { label: 'AI engine active', state: 'online' };
    }
  }
  const extracts: ProductStatusChip[] = SOURCE_EXTRACTS_LINE.split(' · ').map((segment) => ({
    label: segment.trim(),
    state: 'online',
  }));
  return [
    { label: byPrefix.dcs?.label ?? 'DCS online', state: byPrefix.dcs?.state ?? 'online' },
    {
      label: byPrefix.historian?.label ?? 'Historian · connecting',
      state: byPrefix.historian?.state ?? 'pending',
      title: byPrefix.historian?.title,
    },
    {
      label: cadence === 'osa' ? 'LIMS: OSA online' : (byPrefix.lims?.label ?? 'LIMS: manual assays'),
      state: cadence === 'osa' ? 'online' : (byPrefix.lims?.state ?? 'pending'),
      labelVisible: cadence === 'osa',
    },
    {
      label: byPrefix.design?.label ?? 'Design basis loaded',
      state: byPrefix.design?.state ?? 'online',
    },
    ...extracts,
    { label: byPrefix.ai?.label ?? 'AI engine active', state: byPrefix.ai?.state ?? 'online' },
  ];
}

export function productBarIdentity(persona: Persona): ProductBarIdentity {
  const roleId = plantRoleIdFromPersona(persona) ?? defaultPlantRole();
  const role = PLANT_ROLES.find((item) => item.id === roleId) ?? PLANT_ROLES[0];
  const personName = DECK_CONTACT_NAMES[role.id];
  const label = personName ? `${personName} · ${role.label}` : role.label;
  const initial = personName ? personName.charAt(0) : role.label.charAt(0).toUpperCase();
  return {
    initial,
    label,
    title: 'Role is assigned by the site — switch personas from the review rail',
  };
}
